"use client";

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

type ProgressStatus = "loading" | "success" | "error";

type StartParams = {
  title?: string;
  total: number;
  onComplete?: () => void;
  itemLabelPlural?: string;
  baseItemUrl?: string;
  message?: string;
};

export type ProgressContextType = {
  active: boolean;
  title?: string;
  current: number;
  total: number;
  status: ProgressStatus;
  itemLabelPlural: string;
  baseItemUrl?: string;
  start: (params: StartParams) => void;
  inc: () => void;
  abort: () => void;
  setError: (buttonLabel?: string) => void;
  setSuccess: (buttonLabel?: string) => void;
  close: () => void;
  backdropVisible: boolean;
  backdropLabel?: string;
  showBackdrop: (label?: string) => void;
  hideBackdrop: () => void;
  message?: string;
  setMessage: (msg?: string) => void;
  buttonLabel?: string;
  setButtonLabel: (label?: string) => void;
};

const ProgressContext = createContext<ProgressContextType | null>(null);

export function ProgressProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState<string | undefined>();
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<ProgressStatus>("loading");
  const [itemLabelPlural, setItemLabelPlural] = useState("Eintrag");
  const [baseItemUrl, setBaseItemUrl] = useState<string | undefined>();
  const [message, setMessage] = useState<string | undefined>();

  const [backdropVisible, setBackdropVisible] = useState(false);
  const [backdropLabel, setBackdropLabel] = useState<string | undefined>();

  const [buttonLabel, setButtonLabel] = useState<string | undefined>();

  const onCompleteRef = useRef<(() => void) | null>(null);

  const start = useCallback(
    ({
      title,
      total,
      onComplete,
      itemLabelPlural,
      baseItemUrl,
      message,
    }: StartParams) => {
      setTitle(title);
      setTotal(total);
      setCurrent(0);
      setStatus("loading");
      setItemLabelPlural(itemLabelPlural || "Eintrag");
      setBaseItemUrl(baseItemUrl);
      setMessage(message);
      setActive(true);
      onCompleteRef.current = onComplete || null;
    },
    []
  );

  const inc = useCallback(() => {
    setCurrent((prev) => prev + 1);
  }, []);

  const abort = useCallback(() => {
    setActive(false);
  }, []);

  const setError = useCallback(() => {
    setStatus("error");
  }, []);

  const setSuccess = useCallback(() => {
    setStatus("success");
  }, []);

  const close = useCallback(() => {
    setActive(false);
    setTitle(undefined);
    setCurrent(0);
    setTotal(0);
    setStatus("loading");
    setItemLabelPlural("Eintrag");
    setBaseItemUrl(undefined);
    setMessage(undefined);

    if (onCompleteRef.current) {
      onCompleteRef.current();
      onCompleteRef.current = null;
    }
  }, []);

  const showBackdrop = useCallback((label?: string) => {
    setBackdropVisible(true);
    setBackdropLabel(label);
  }, []);

  const hideBackdrop = useCallback(() => {
    setBackdropVisible(false);
    setBackdropLabel(undefined);
  }, []);

  return (
    <ProgressContext.Provider
      value={{
        active,
        title,
        current,
        total,
        status,
        itemLabelPlural,
        baseItemUrl,
        start,
        inc,
        abort,
        setError,
        setSuccess,
        close,
        backdropVisible,
        backdropLabel,
        showBackdrop,
        hideBackdrop,
        message,
        setMessage,
        buttonLabel,
        setButtonLabel,
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress(): ProgressContextType {
  const ctx = useContext(ProgressContext);
  if (!ctx) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return ctx;
}
